import { signal } from "@preact/signals";
import type { Account } from "~/protocol/auth";
import { identifyUser, resetUser } from "~/web/src/analytics";
import { reportError } from "~/web/src/errors";
import { keepFirstTouch } from "~/web/src/first-touch";

// The client half of auth (spec: docs/project/own-auth-spec.md, decision 12):
// the session is a cookie the server set, so the gate asks GET /api/me once
// and the router gates on the answer. Signing in is the static /login page
// (a mailed link); signing out is one POST and a full navigation home.

export type AuthState = "loading" | "signed_out" | "ready";

export const authState = signal<AuthState>("loading");

/** Who is signed in, for our own menu and the banner; non-null exactly while authState is "ready". */
export const account = signal<Account | null>(null);

let started: Promise<void> | null = null;

/** Asks the server who the cookie is and keeps authState in sync. Idempotent. */
export function startAuth(): Promise<void> {
  started ??= (async () => {
    const res = await fetch("/api/me", { cache: "no-store" });
    // The cookie lands before anything leaves this page or calls /api: a tab that goes to
    // /login keeps its touch, and the pages' first call carries it.
    await keepFirstTouch();
    if (res.status === 401) {
      account.value = null;
      authState.value = "signed_out";
      return;
    }
    if (!res.ok) throw new Error(`/api/me answered ${res.status}`);
    const me = (await res.json()) as Account;
    identifyUser(me.authId, {
      email: me.email,
      name: me.name,
      orgId: me.org.id,
      orgName: me.org.name,
    });
    account.value = me;
    authState.value = "ready";
  })().catch((err: unknown) => {
    started = null;
    reportError(err);
  });
  return started;
}

/** The login page, back to `returnTo` once the mailed link is clicked. On the watch host
 * (own-auth-spec decision 14) the page names the app's handoff route instead: the app signs the
 * browser in — through /login if it must — and sends it back here with a token for this host. */
export function signIn(returnTo: string): void {
  const url = new URL(returnTo, location.origin);
  const next = `${url.pathname}${url.search}${url.hash}`;
  const handoff = document
    .querySelector('meta[name="featurette-handoff"]')
    ?.getAttribute("content")
    ?.trim();
  const base = handoff || "/login";
  location.assign(`${base}?next=${encodeURIComponent(next)}`);
}

/** Ends the session and lands on the landing page (a gated page would just bounce to sign-in).
 *  Analytics resets first: the redirect is a full navigation, so nothing runs after it. */
export async function signOut(): Promise<void> {
  resetUser();
  await fetch("/api/auth/logout", { method: "POST" }).catch(reportError);
  location.assign("/");
}

/** Asks for the verification mail again; the server's sentence when it refuses (the per-minute gap). */
export async function resendVerification(): Promise<string | null> {
  const res = await fetch("/api/auth/resend", { method: "POST" });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? `Couldn't send the mail (${res.status}).`;
}
